import { Component, Input, OnDestroy, OnInit, ChangeDetectionStrategy, ChangeDetectorRef, Output, EventEmitter } from '@angular/core';
import { Chart } from 'angular-highcharts';
import { SeriesOptionsType } from 'highcharts';
import { ElementBaseComponent } from '../ElementBaseComponent';
import { HelpTextService } from '../../../services/services-index';
import { Column, ColumnModel, DataSet, ElementMetric } from '../../../models/models-index';

@Component({
  selector: 'scorecard-billboard-element',
  templateUrl: 'scorecard-billboard-element.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  styleUrls: ['../panel-elements.scss', './scorecard-billboard-element.component.scss']
})

export class ScorecardBillboardElementComponent extends ElementBaseComponent implements OnDestroy, OnInit {
  @Input() set dataSets(value: DataSet[]) {
    if (!!value && value.length > 0) {
      this.processData(value);
      this.isDataSetLoaded$.next(true);
    } else {
      this.isDataSetLoaded$.next(false);
    }
  }
  cardTitle: string;
  primaryMetricName: string;
  primaryMetricValue: string;
  primaryMetricTrendColor: string;
  primaryMetricTrendArrow: string;
  leftMetricName: string;
  leftMetricValue: any;
  leftMetricTrendColor: string;
  leftMetricTrendArrow: string;
  centerMetricName: string;
  centerMetricValue: any;
  centerMetricTrendColor: string;
  centerMetricTrendArrow: string;
  rightMetricName: string;
  rightMetricValue: any;
  rightMetricTrendColor: string;
  rightMetricTrendArrow: string;
  seriesData: any;
  chartLineColor: string;
  chartFillColor: string;
  currentChart: Chart;
  get entityName(): string {
    return this.settings.options['entityName'];
  }

  private readonly DateColumnName = 'Date';

  constructor(
    helpTextService: HelpTextService
  ) {
    super(helpTextService);
  }

  ngOnInit() {
    if (!this.settings.options['entityName']) {
      throw Error('Entity name is not configured for the scorecard billboard');
    }
  }

  processData(dataSets: DataSet[]) {
    console.log('ScorecardBillboardElementComponent.processData', dataSets);

    const [
      mainData,
      chartData,
      dimensionName,
      dimensionIndex,
      dateColumnIndex,
      chartDimensionIndex,
      chartMetricIndex,
      metricDetails
    ] = this.ExtractColumnConfigs(dataSets);

    // filter to entity specific row
    const mainRow = mainData.rows.find(row => {
      const dimensionValue = row[dimensionIndex]?.value;
      return dimensionValue === this.settings.options['entityName'];
    });

    if (!mainRow) {
      throw Error(`No main data found for entity ${this.settings.options['entityName']}`);
    }

    // filter to entity specific chart rows
    const chartRows = chartData.rows.filter(row => {
      const dimensionValue = row[chartDimensionIndex]?.value;
      return dimensionValue === this.settings.options['entityName'];
    });
    if (chartRows.length === 0) {
      throw Error(`No chart data found for entity ${this.settings.options['entityName']}`);
    }

    // grab the value of the dimension column from the first dataset
    this.cardTitle = mainRow[dimensionIndex]?.value.toString();

    const [primaryMetric, leftMetric, centerMetric, rightMetric] = metricDetails;

    console.log('metricDetails', metricDetails);

    // primary
    this.primaryMetricName = primaryMetric.elementMetric.title ?? primaryMetric.column.displayName ?? primaryMetric.column.name;
    this.primaryMetricValue = mainRow[primaryMetric.index].label ?? mainRow[primaryMetric.index]?.value?.toString();
    this.primaryMetricTrendColor = this.getTrendIconColor(<number>mainRow[primaryMetric.trendIndex].value);
    this.primaryMetricTrendArrow = this.getTrendArrowIconClass(<number>mainRow[primaryMetric.trendIndex].value);

    this.chartFillColor = this.getChartTrendFillColor(<number>mainRow[primaryMetric.trendIndex].value);
    this.chartLineColor = this.getChartTrendLineColor(<number>mainRow[primaryMetric.trendIndex].value);

    // left
    this.leftMetricName = leftMetric.elementMetric.title ?? leftMetric.column.displayName ?? leftMetric.column.name;
    this.leftMetricValue = mainRow[leftMetric.index].label ?? mainRow[leftMetric.index]?.value;
    this.leftMetricTrendColor = this.getTrendIconColor(<number>mainRow[leftMetric.trendIndex].value);
    this.leftMetricTrendArrow = this.getTrendArrowIconClass(<number>mainRow[leftMetric.trendIndex].value);

    // center
    this.centerMetricName = centerMetric.elementMetric.title ?? centerMetric.column.displayName ?? centerMetric.column.name;
    this.centerMetricValue = mainRow[centerMetric.index].label ?? mainRow[centerMetric.index]?.value;
    this.centerMetricTrendColor = this.getTrendIconColor(<number>mainRow[centerMetric.trendIndex].value); // updated this line
    this.centerMetricTrendArrow = this.getTrendArrowIconClass(<number>mainRow[centerMetric.trendIndex].value); // added this line

    // right
    this.rightMetricName = rightMetric.elementMetric.title ?? rightMetric.column.displayName ?? rightMetric.column.name;
    this.rightMetricValue = mainRow[rightMetric.index].label ?? mainRow[rightMetric.index]?.value;
    this.rightMetricTrendColor = this.getTrendIconColor(<number>mainRow[rightMetric.trendIndex].value); // updated this line
    this.rightMetricTrendArrow = this.getTrendArrowIconClass(<number>mainRow[rightMetric.trendIndex].value); // updated this line

    // Build chart series data from the second dataset using the dimension column
    this.seriesData = chartRows.map(row => ({
      x: row[this.DateColumnName]?.value,
      y: row[chartMetricIndex]?.value
    }));


    this.currentChart = this.generateChart();
  }

  private ExtractColumnConfigs(dataSets: DataSet[]): [
    DataSet,           // mainData
    DataSet,           // chartData
    string,            // dimensionName
    number,            // dimensionIndex
    number,            // dateColumnIndex
    number,            // chartDimensionIndex
    number,            // chartMetricIndex
    { elementMetric: ElementMetric, column: Column, index: number, trendIndex: number }[], // metricDetails
  ] {
    // Expect two datasets: main data (metrics values) and chart series data
    if (!dataSets || dataSets.length < 2) {
      throw Error('Two datasets are required: one for metrics and one for chart series.');
    }
    const mainData = dataSets[0];
    const chartData = dataSets[1];

    // The first dimension name is used for identifying the key column of the rows that indicate the entity being measured
    const dimensionName = this.settings.dimensionNames[0];
    if (!dimensionName) {
      throw Error('Dimension name is not defined for the scorecard billboard');
    }

    if (this.settings.metrics.length < 4) {
      throw Error('Not enough metrics configured for the scorecard billboard');
    }

    const dimensionColumnIndex = mainData.columns.findIndex((col: any) => col.name === dimensionName);
    if (dimensionColumnIndex < 0) {
      throw Error(`Dimension name ${dimensionName} index is not found in the main data`);
    }

    // The second dataset is expected to have the same dimension name as the first dataset and should also have a 'Date' column
    const dateColumn = chartData.columns.find((col: any) => col.name === this.DateColumnName);
    if (!dateColumn) {
      throw Error('Date column is not found in the chart data');
    }
    const dateColumnIndex = chartData.columns.findIndex((col: any) => col.name === this.DateColumnName);
    if (dateColumnIndex < 0) {
      throw Error('Date column index is not found in the chart data');
    }

    // The chart data should have the same dimension name as the first dataset
    const chartDimensionColumn = chartData.columns.find((col: any) => col.name === dimensionName);
    if (!chartDimensionColumn) {
      throw Error(`Dimension name ${dimensionName} is not found in the chart data`);
    }
    const chartDimensionIndex = chartData.columns.findIndex((col: any) => col.name === dimensionName);
    if (chartDimensionIndex < 0) {
      throw Error(`Dimension name ${dimensionName} index is not found in the chart data`);
    }

    const chartMetricIndex = chartData.columns.findIndex((col: any) => col.name === this.settings.metrics[0].name);
    if (chartMetricIndex < 0) {
      throw Error(`Primary metric name ${this.settings.metrics[0].name} index is not found in the chart data`);
    }

    // Validate primary metric
    const primaryMetric = this.settings.metrics[0];
    const primaryMetricColumn = mainData.columns.find((col: any) => col.name === primaryMetric.name);
    if (!primaryMetricColumn) {
      throw Error(`Primary metric name ${primaryMetric.name} is not found in the main data`);
    }
    const primaryMetricIndex = mainData.columns.findIndex((col: any) => col.name === primaryMetric.name);
    if (primaryMetricIndex < 0) {
      throw Error(`Primary metric name ${primaryMetric.name} index is not found in the main data`);
    }

    const primaryMetricTrendIndex = mainData.columns.findIndex((col: any) => col.name === primaryMetric.trend);
    if (primaryMetricTrendIndex < 0) {
      throw Error(`Primary metric trend name ${primaryMetric.trend} index is not found in the main data`);
    }

    // Validate left metric
    const leftMetric = this.settings.metrics[1];
    const leftMetricColumn = mainData.columns.find((col: any) => col.name === leftMetric.name);
    if (!leftMetricColumn) {
      throw Error(`Left metric name ${leftMetric.name} is not found in the main data`);
    }
    const leftMetricIndex = mainData.columns.findIndex((col: any) => col.name === leftMetric.name);
    if (leftMetricIndex < 0) {
      throw Error(`Left metric index for ${leftMetric.name} is not found in the main data`);
    }

    const leftMetricTrendIndex = mainData.columns.findIndex((col: any) => col.name === leftMetric.trend);
    if (leftMetricTrendIndex < 0) {
      throw Error(`Left metric trend name ${leftMetric.trend} index is not found in the main data`);
    }

    // Validate center metric
    const centerMetric = this.settings.metrics[2];
    const centerMetricColumn = mainData.columns.find((col: any) => col.name === centerMetric.name);
    if (!centerMetricColumn) {
      throw Error(`Center metric name ${centerMetric.name} is not found in the main data`);
    }
    const centerMetricIndex = mainData.columns.findIndex((col: any) => col.name === centerMetric.name);
    if (centerMetricIndex < 0) {
      throw Error(`Center metric index for ${centerMetric.name} is not found in the main data`);
    }
    const centerMetricTrendIndex = mainData.columns.findIndex((col: any) => col.name === centerMetric.trend);
    if (centerMetricTrendIndex < 0) {
      throw Error(`Center metric trend name ${centerMetric.trend} index is not found in the main data`);
    }

    // Validate right metric
    const rightMetric = this.settings.metrics[3];
    const rightMetricColumn = mainData.columns.find((col: any) => col.name === rightMetric.name);
    if (!rightMetricColumn) {
      throw Error(`Right metric name ${rightMetric.name} is not found in the main data`);
    }
    const rightMetricIndex = mainData.columns.findIndex((col: any) => col.name === rightMetric.name);
    if (rightMetricIndex < 0) {
      throw Error(`Right metric index for ${rightMetric.name} is not found in the main data`);
    }
    const rightMetricTrendIndex = mainData.columns.findIndex((col: any) => col.name === rightMetric.trend);
    if (rightMetricTrendIndex < 0) {
      throw Error(`Right metric trend name ${rightMetric.trend} index is not found in the main data`);
    }


    if (primaryMetricIndex < 0 || leftMetricIndex < 0 || centerMetricIndex < 0 || rightMetricIndex < 0) {
      throw Error('One or more metric indices are invalid.');
    }

    return [
      mainData,             // mainData
      chartData,            // chartData
      dimensionName,        // dimensionName
      dimensionColumnIndex, // dimensionIndex
      dateColumnIndex,      // dateColumnIndex
      chartDimensionIndex,  // chartDimensionIndex,
      chartMetricIndex,     // chartMetricIndex
      [
        { elementMetric: primaryMetric, column: primaryMetricColumn, index: primaryMetricIndex, trendIndex: primaryMetricTrendIndex },
        { elementMetric: leftMetric, column: leftMetricColumn, index: leftMetricIndex, trendIndex: leftMetricTrendIndex },
        { elementMetric: centerMetric, column: centerMetricColumn, index: centerMetricIndex, trendIndex: centerMetricTrendIndex },
        { elementMetric: rightMetric, column: rightMetricColumn, index: rightMetricIndex, trendIndex: rightMetricTrendIndex }
      ]
    ];
  }

  ngOnDestroy(): void { }

  private generateChart() {
    const chartSeries: SeriesOptionsType[] = [{
      type: 'area',
      data: this.seriesData,
      color: this.chartLineColor,
      fillColor: this.chartFillColor,
      lineWidth: 2,
      marker: {
        enabled: true,
        symbol: 'circle',
        radius: 3,
        color: this.chartLineColor
      },
      states: {
        hover: {
          enabled: false
        }
      }
    }];

    var chart = new Chart({
      credits: {
        enabled: false
      },
      title: {
        text: ''
      },
      chart: {
        showAxes: false,
        height: 150
      },
      tooltip: {
        enabled: false
      },
      // AXES.....
      xAxis: {
        title: { text: null },
        lineWidth: 0,
        minorGridLineWidth: 0,
        lineColor: 'transparent',
        labels: {
          enabled: false
        },
        minorTickLength: 0,
        tickLength: 0,
      },
      yAxis: {
        title: { text: null },
        lineWidth: 0,
        gridLineColor: 'transparent',
        minorGridLineWidth: 0,
        lineColor: 'transparent',
        labels: {
          enabled: false
        },
        minorTickLength: 0,
        tickLength: 0
      },
      legend: {
        enabled: false
      },
      series: chartSeries
    });
    return chart;
  }

  getChartTrendFillColor(trend: number): string {
      return this.isTrendUp(trend) ? '#EFF6F1' : '#F9EEED';
  }

  getChartTrendLineColor(trend: number): string {
    return this.isTrendUp(trend) ? '#C7E0CD' : '#EAC2C0';
}
}
